import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Users, ShieldCheck, Loader2, Download, AlertTriangle, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { toast } from 'sonner';

const formatMinutes = (m) => {
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const ageColor = (m) => {
  if (m == null) return 'text-[#8a8579]';
  if (m > 120) return 'text-[#b91c1c] font-medium';
  if (m > 60) return 'text-[#92400e] font-medium';
  return 'text-[#3a3a3a]';
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (d) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

const AdminWorkload = () => {
  const [tab, setTab] = useState('agent'); // 'agent' | 'territory_manager'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [fromDate, setFromDate] = useState(daysAgoIso(30));
  const [toDate, setToDate] = useState(todayIso());
  const [downloading, setDownloading] = useState(false);
  const [ops, setOps] = useState(null);
  const [opsDays, setOpsDays] = useState(7);

  const loadOps = useCallback(async (d = opsDays) => {
    try {
      const r = await api.get('/admin/operations-stats', { params: { days: d } });
      setOps(r.data);
    } catch (e) { console.error('admin/operations-stats failed', e); }
  }, [opsDays]);
  useEffect(() => { loadOps(opsDays); }, [loadOps, opsDays]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/workload', { params: { role: tab } });
      setRows(r.data || []);
    } catch {
      toast.error('No se pudo cargar la carga del equipo');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  const totals = rows.reduce((acc, r) => {
    acc.users += 1;
    acc.pending += r.pending_count || 0;
    acc.acked24h += r.acked_24h || 0;
    if (r.pending_count > 0) acc.usersWithBacklog += 1;
    return acc;
  }, { users: 0, pending: 0, acked24h: 0, usersWithBacklog: 0 });

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const fromIso = new Date(fromDate + 'T00:00:00').toISOString();
      const toIso = new Date(toDate + 'T23:59:59').toISOString();
      const r = await api.get('/admin/notifications-report', {
        params: { role: tab, from: fromIso, to: toIso },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notificaciones_${tab}_${fromDate.replace(/-/g, '')}_${toDate.replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch {
      toast.error('Error al descargar reporte');
    } finally {
      setDownloading(false);
    }
  };

  const TabBtn = ({ k, label, icon: Icon, count }) => (
    <button
      onClick={() => setTab(k)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] transition border ${tab === k ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white text-[#3a3a3a] border-[#e8e5de] hover:bg-[#f3f0e9]'}`}
      data-testid={`workload-tab-${k}`}
    >
      <Icon size={13} strokeWidth={1.7}/>
      {label}
      {count > 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === k ? 'bg-white/15 text-white' : 'bg-[#b91c1c] text-white'}`} data-testid={`workload-tab-${k}-badge`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="admin-workload-page">
      {/* Header + tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TabBtn k="agent" label="Agentes" icon={ShieldCheck} count={tab === 'agent' ? totals.usersWithBacklog : 0}/>
          <TabBtn k="territory_manager" label="Territory Managers" icon={Users} count={tab === 'territory_manager' ? totals.usersWithBacklog : 0}/>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="kpi-users">
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">{tab === 'agent' ? 'Agentes activos' : 'TMs activos'}</p>
          <p className="text-[26px] font-serif tracking-tight mt-1">{totals.users}</p>
        </div>
        <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="kpi-backlog">
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Con backlog</p>
          <p className={`text-[26px] font-serif tracking-tight mt-1 ${totals.usersWithBacklog > 0 ? 'text-[#b91c1c]' : ''}`}>{totals.usersWithBacklog}</p>
        </div>
        <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="kpi-pending">
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Notifs pendientes</p>
          <p className="text-[26px] font-serif tracking-tight mt-1">{totals.pending}</p>
        </div>
        <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="kpi-acked">
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Confirmadas · 24h</p>
          <p className="text-[26px] font-serif tracking-tight mt-1 text-[#15803d]">{totals.acked24h}</p>
        </div>
      </div>

      {/* Excel export */}
      <div className="bg-white border border-[#e8e5de] rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="report-section">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#8a8579] mb-1">Reporte de tiempo de respuesta</p>
          <p className="text-[12px] text-[#3a3a3a]">Excel con detalle por notificación + resumen por usuario</p>
        </div>
        <div className="flex-1"/>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Desde</label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 text-[12.5px] w-[150px]" data-testid="report-from"/>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Hasta</label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 text-[12.5px] w-[150px]" data-testid="report-to"/>
        </div>
        <Button
          onClick={downloadReport}
          disabled={downloading}
          className="h-9 rounded-full bg-[#15803d] hover:bg-[#166534] text-white text-[12.5px] px-4"
          data-testid="report-download"
        >
          {downloading ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <Download size={13} className="mr-1.5"/>}
          {downloading ? 'Generando…' : 'Descargar Excel'}
        </Button>
      </div>

      {/* Operations stats — approver + evidence + tracker punctuality */}
      {ops && (
        <div className="space-y-3" data-testid="ops-stats-section">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-serif text-[22px] tracking-tight leading-tight">Estadísticas de operación</h3>
            <select
              value={opsDays}
              onChange={(e) => setOpsDays(Number(e.target.value))}
              className="h-8 text-[12px] border border-[#e8e5de] rounded-md px-2 bg-white"
              data-testid="ops-days-select"
            >
              <option value={1}>Hoy</option>
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="ops-approver">
              <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Tiempo aprobador</p>
              <p className="font-serif text-[34px] tabular-nums leading-none mt-1">
                {ops.approver_response.median_min != null ? `${ops.approver_response.median_min}m` : '—'}
              </p>
              <p className="text-[11px] text-[#5a5a5a] mt-1.5">mediana · {ops.approver_response.count} aprobaciones</p>
              <div className="text-[10.5px] text-[#8a8579] mt-1.5 flex gap-3">
                <span>p90: <strong className="text-[#3a3a3a] tabular-nums">{ops.approver_response.p90_min != null ? `${ops.approver_response.p90_min}m` : '—'}</strong></span>
                <span>aprobadas: <strong className="text-[#15803d] tabular-nums">{ops.approver_response.approved}</strong></span>
                <span>rechazadas: <strong className="text-[#991b1b] tabular-nums">{ops.approver_response.rejected}</strong></span>
              </div>
            </div>
            <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="ops-evidence">
              <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Tiempo evidencia (monolito)</p>
              <p className="font-serif text-[34px] tabular-nums leading-none mt-1">
                {ops.evidence_send.median_min != null ? `${ops.evidence_send.median_min}m` : '—'}
              </p>
              <p className="text-[11px] text-[#5a5a5a] mt-1.5">mediana · {ops.evidence_send.confirmed} confirmadas</p>
              <div className="text-[10.5px] text-[#8a8579] mt-1.5 flex gap-3">
                <span>p90: <strong className="text-[#3a3a3a] tabular-nums">{ops.evidence_send.p90_min != null ? `${ops.evidence_send.p90_min}m` : '—'}</strong></span>
                <span>pendientes: <strong className="text-[#92400e] tabular-nums">{ops.evidence_send.pending}</strong></span>
              </div>
            </div>
            <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="ops-ocr">
              <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">OCR discrepancias</p>
              <p className="font-serif text-[34px] tabular-nums leading-none mt-1 text-[#991b1b]">
                {ops.ocr_mismatches_recent?.length || 0}
              </p>
              <p className="text-[11px] text-[#5a5a5a] mt-1.5">monolitos con precios distintos al aprobado</p>
            </div>
          </div>

          {/* Tracker punctuality */}
          <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden" data-testid="ops-punctuality">
            <div className="px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] flex items-center justify-between">
              <p className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Puntualidad de trackers</p>
              <span className="text-[10.5px] text-[#a8a397]">slot ±30 min = a tiempo · &gt;30 min = tarde · sin captura = no realizada</span>
            </div>
            {(ops.tracker_punctuality || []).length === 0 ? (
              <p className="py-6 text-center text-[12.5px] text-[#8a8579]">Sin slots evaluables en este rango.</p>
            ) : (
              <div className="divide-y divide-[#f0ede5]">
                <div className="grid grid-cols-[1fr_80px_80px_80px_120px] gap-3 px-4 py-2 text-[10.5px] uppercase tracking-wider text-[#8a8579]">
                  <div>Tracker</div>
                  <div className="text-right">A tiempo</div>
                  <div className="text-right">Tarde</div>
                  <div className="text-right">No realizada</div>
                  <div className="text-right">% a tiempo</div>
                </div>
                {ops.tracker_punctuality.map(t => {
                  const cls = t.on_time_pct >= 80 ? 'text-[#15803d]' : t.on_time_pct >= 50 ? 'text-[#92400e]' : 'text-[#991b1b]';
                  return (
                    <div key={t.user_id} className="grid grid-cols-[1fr_80px_80px_80px_120px] gap-3 items-center px-4 py-2.5 text-[12.5px]" data-testid={`punctuality-${t.user_id}`}>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{t.name || '(sin nombre)'}</p>
                        <p className="text-[10.5px] text-[#8a8579] truncate">{t.email}</p>
                      </div>
                      <div className="text-right tabular-nums text-[#15803d]">{t.on_time}</div>
                      <div className="text-right tabular-nums text-[#92400e]">{t.late}</div>
                      <div className="text-right tabular-nums text-[#991b1b]">{t.missed}</div>
                      <div className={`text-right tabular-nums font-medium ${cls}`}>{t.on_time_pct}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_110px_140px_120px_40px] gap-3 px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] text-[10.5px] uppercase tracking-wider text-[#8a8579]">
          <div>Usuario</div>
          <div className="text-right">Pendientes</div>
          <div className="text-right">Más antigua</div>
          <div className="text-right">Acked · 24h</div>
          <div/>
        </div>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-[13px] text-[#8a8579]">Sin usuarios para este rol.</div>
        ) : (
          <div className="divide-y divide-[#f0ede5]">
            {rows.map(r => {
              const isOpen = !!expanded[r.user_id];
              const hasBacklog = (r.pending_count || 0) > 0;
              return (
                <div key={r.user_id} data-testid={`workload-row-${r.user_id}`}>
                  <button
                    onClick={() => setExpanded({ ...expanded, [r.user_id]: !isOpen })}
                    className="w-full grid grid-cols-[1fr_110px_140px_120px_40px] gap-3 items-center px-4 py-3 text-left hover:bg-[#fafaf7] transition"
                    data-testid={`workload-toggle-${r.user_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium leading-tight truncate flex items-center gap-2">
                        {hasBacklog && r.oldest_pending_minutes > 120 && <AlertTriangle size={12} className="text-[#b91c1c] shrink-0"/>}
                        {r.name || '(sin nombre)'}
                      </p>
                      <p className="text-[11px] text-[#8a8579] truncate">{r.email}</p>
                    </div>
                    <div className={`text-right font-mono tabular-nums text-[14px] ${hasBacklog ? 'text-[#b91c1c] font-medium' : 'text-[#8a8579]'}`}>
                      {r.pending_count || 0}
                    </div>
                    <div className={`text-right text-[12.5px] tabular-nums inline-flex items-center justify-end gap-1 ${ageColor(r.oldest_pending_minutes)}`}>
                      {hasBacklog && <Clock size={11}/>}
                      {hasBacklog ? formatMinutes(r.oldest_pending_minutes) : '—'}
                    </div>
                    <div className="text-right text-[13px] tabular-nums text-[#15803d]">{r.acked_24h || 0}</div>
                    <div className="text-right text-[#a8a397]">{isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 -mt-1 bg-[#fafaf7]" data-testid={`workload-detail-${r.user_id}`}>
                      {(r.pending_sample && r.pending_sample.length > 0) ? (
                        <div className="border border-[#e8e5de] rounded-lg overflow-hidden">
                          <div className="grid grid-cols-[120px_1fr_120px] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#8a8579] bg-white border-b border-[#e8e5de]">
                            <div>Tipo</div>
                            <div>Notificación</div>
                            <div className="text-right">Antigüedad</div>
                          </div>
                          {r.pending_sample.map(n => {
                            const created = n.created_at ? new Date(n.created_at) : null;
                            const minutes = created ? Math.floor((Date.now() - created.getTime()) / 60000) : null;
                            return (
                              <div key={n.id} className="grid grid-cols-[120px_1fr_120px] gap-2 px-3 py-2 text-[12px] bg-white border-b border-[#f0ede5] last:border-b-0" data-testid={`workload-notif-${n.id}`}>
                                <div className="text-[10.5px] font-mono text-[#8a8579] truncate">{n.type}</div>
                                <div className="min-w-0">
                                  <p className="truncate">{n.title}</p>
                                  {n.body && <p className="text-[11px] text-[#8a8579] truncate">{n.body}</p>}
                                </div>
                                <div className={`text-right tabular-nums text-[11.5px] ${ageColor(minutes)}`}>{formatMinutes(minutes)}</div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-[11.5px] text-[#8a8579] py-1.5">Sin notificaciones pendientes 🎉</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWorkload;
